import { useEffect, useState, useRef } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Archive, FileText, Youtube, Video, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyArticle = {
  title: "", category: "General", content: "", tags: "",
  language: "en", video_url: "",
};

export default function KnowledgeBaseTab({ t }) {
  const [articles, setArticles] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newArticle, setNewArticle] = useState(emptyArticle);
  const [editArticle, setEditArticle] = useState(null);
  const [uploading, setUploading] = useState(null); // article_id currently uploading
  const fileInputs = useRef({});

  const load = async () => {
    try {
      const res = await ax.get(`${API}/admin/kb`);
      setArticles(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load KB");
    }
  };
  useEffect(() => { load(); }, []);

  const createArticle = async () => {
    if (!newArticle.title.trim()) return toast.error("Title required");
    try {
      const payload = {
        ...newArticle,
        tags: newArticle.tags ? newArticle.tags.split(",").map((x) => x.trim()).filter(Boolean) : [],
      };
      await ax.post(`${API}/admin/kb`, payload);
      toast.success("Article created");
      setNewArticle(emptyArticle);
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        title: editArticle.title,
        category: editArticle.category,
        content: editArticle.content,
        language: editArticle.language,
        video_url: editArticle.video_url || "",
        tags: Array.isArray(editArticle.tags)
          ? editArticle.tags
          : (editArticle.tags || "").split(",").map((x) => x.trim()).filter(Boolean),
      };
      await ax.put(`${API}/admin/kb/${editArticle.article_id}`, payload);
      toast.success("Article updated");
      setEditArticle(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const archiveArticle = async (a) => {
    if (!window.confirm(`Archive article "${a.title}"?`)) return;
    try {
      await ax.delete(`${API}/admin/kb/${a.article_id}`);
      toast.success("Article archived");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadPDF = async (articleId, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Only PDF files allowed");
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25 MB");
    setUploading(articleId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await ax.post(`${API}/admin/kb/${articleId}/upload-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF uploaded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const removePDF = async (a) => {
    if (!window.confirm("Remove attached PDF?")) return;
    try {
      await ax.delete(`${API}/admin/kb/${a.article_id}/pdf`);
      toast.success("PDF removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="kb-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Knowledge Base Management</h3>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs font-bold tracking-[0.1em] uppercase" data-testid="create-kb-btn">
              <Plus size={14} className="mr-1" /> New Article
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader><DialogTitle>Create KB Article</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Title" value={newArticle.title} onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })} data-testid="kb-title-input" />
              <Input className="rounded-none" placeholder="Category (e.g. Access Control, Radar Systems)" value={newArticle.category} onChange={(e) => setNewArticle({ ...newArticle, category: e.target.value })} data-testid="kb-category-input" />
              <Textarea className="rounded-none h-32" placeholder="Content" value={newArticle.content} onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })} data-testid="kb-content-input" />
              <Input className="rounded-none" placeholder="YouTube or Vimeo URL (optional)" value={newArticle.video_url} onChange={(e) => setNewArticle({ ...newArticle, video_url: e.target.value })} data-testid="kb-video-input" />
              <Input className="rounded-none" placeholder="Tags (comma-separated)" value={newArticle.tags} onChange={(e) => setNewArticle({ ...newArticle, tags: e.target.value })} data-testid="kb-tags-input" />
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={createArticle} data-testid="kb-submit-btn">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {articles.map((a) => (
          <div key={a.article_id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`kb-card-${a.article_id}`}>
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-[#0A0A0A]">{a.title}</h4>
                <div className="flex gap-2 items-center mt-1">
                  <Badge variant="outline" className="rounded-none text-[10px] font-bold">{a.category}</Badge>
                  <Badge variant="outline" className="rounded-none text-[10px] font-bold uppercase">{a.language || "en"}</Badge>
                  {a.video_type === "youtube" && <span className="flex items-center gap-1 text-[10px] text-[#FF2A2A] font-bold"><Youtube size={12} />YouTube</span>}
                  {a.video_type === "vimeo" && <span className="flex items-center gap-1 text-[10px] text-[#00C853] font-bold"><Video size={12} />Vimeo</span>}
                  {a.pdf_file_id && <span className="flex items-center gap-1 text-[10px] text-[#002FA7] font-bold"><FileText size={12} />PDF</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setEditArticle({ ...a, tags: (a.tags || []).join(", ") })} data-testid={`edit-kb-${a.article_id}`}>
                  <Pencil size={12} />
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-7 text-[#FF2A2A] hover:bg-[#FF2A2A]/10" onClick={() => archiveArticle(a)} data-testid={`archive-kb-${a.article_id}`}>
                  <Archive size={12} />
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#4B5563] line-clamp-3 mb-3">{a.content}</p>
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E5E7EB]">
              <input
                type="file"
                accept="application/pdf"
                ref={(el) => (fileInputs.current[a.article_id] = el)}
                onChange={(e) => uploadPDF(a.article_id, e.target.files?.[0])}
                className="hidden"
                data-testid={`kb-pdf-input-${a.article_id}`}
              />
              <Button
                size="sm"
                variant="outline"
                className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase"
                disabled={uploading === a.article_id}
                onClick={() => fileInputs.current[a.article_id]?.click()}
                data-testid={`kb-pdf-upload-${a.article_id}`}
              >
                <Upload size={12} className="mr-1" /> {a.pdf_file_id ? "Replace PDF" : "Upload PDF"}
              </Button>
              {a.pdf_file_id && (
                <>
                  <a
                    href={`${API}/kb/file/${a.pdf_file_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-[#002FA7] underline font-bold"
                    data-testid={`kb-pdf-view-${a.article_id}`}
                  >
                    {a.pdf_filename || "View PDF"}
                  </a>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 text-[#FF2A2A]" onClick={() => removePDF(a)} data-testid={`kb-pdf-remove-${a.article_id}`}>
                    <Trash2 size={12} />
                  </Button>
                </>
              )}
              {a.video_url && (
                <a href={a.video_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#002FA7] underline font-bold" data-testid={`kb-video-link-${a.article_id}`}>
                  {a.video_url}
                </a>
              )}
            </div>
          </div>
        ))}
        {articles.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-8">No articles yet. Create your first one.</p>}
      </div>

      <Dialog open={!!editArticle} onOpenChange={(o) => !o && setEditArticle(null)}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="edit-kb-dialog">
          <DialogHeader><DialogTitle>Edit Article</DialogTitle></DialogHeader>
          {editArticle && (
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Title" value={editArticle.title} onChange={(e) => setEditArticle({ ...editArticle, title: e.target.value })} data-testid="edit-kb-title" />
              <Input className="rounded-none" placeholder="Category" value={editArticle.category} onChange={(e) => setEditArticle({ ...editArticle, category: e.target.value })} data-testid="edit-kb-category" />
              <Textarea className="rounded-none h-32" placeholder="Content" value={editArticle.content} onChange={(e) => setEditArticle({ ...editArticle, content: e.target.value })} data-testid="edit-kb-content" />
              <Input className="rounded-none" placeholder="YouTube or Vimeo URL" value={editArticle.video_url || ""} onChange={(e) => setEditArticle({ ...editArticle, video_url: e.target.value })} data-testid="edit-kb-video" />
              <Input className="rounded-none" placeholder="Tags (comma-separated)" value={editArticle.tags || ""} onChange={(e) => setEditArticle({ ...editArticle, tags: e.target.value })} data-testid="edit-kb-tags" />
              <DialogFooter>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold tracking-[0.1em] uppercase" onClick={saveEdit} data-testid="edit-kb-save">Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
